import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useSessionContext } from "./session.context";
import {
  GetBranchDailyModel,
  GetBranchDailyModelV2,
  GetDailyPerformanceModel,
  GetLateRankingResponse,
  GetPerformanceRankingResponseModel,
  QueryKeyReportBranchDaily,
  QueryKeyReportDailyPerformance,
  QueryKeyReportGetAbsentRanking,
  QueryKeyReportGetAmendRanking,
  QueryKeyReportGetLateRanking,
  QueryKeyReportGetLeaveRanking,
  QueryKeyReportGetOvertimeRanking,
  QueryKeyReportGetUndertimeRanking,
  QueryKeyReportPerformanceRanking,
  getAbsentRanking,
  getAmendedRanking,
  getBranchDaily,
  getBranchDailyV2,
  getDailyPerformance,
  getLateRanking,
  getLeaveRanking,
  getOvertimeRanking,
  getPerformanceRanking,
  getUndertimeRanking,
} from "@core/model/query-model-report";
import { authHeaders } from "./auth-header";
import { AxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getApiErrorMessage } from "@core/utility/get-error-message";
import { appConfig } from "../../app.config";
import { ALL } from "@core/model/const";
import { DashboardPayload } from "@core/model/dashboard-payload.interface";

export function useQyGetReportBranchDaily(
  workDate: string,
  branchId: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetBranchDailyModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (workDate: string, branchId: string) => {
    await checkSession();

    showProgress();
    const operation = await getBranchDaily(workDate, branchId, authHeaders());
    const response = (await operation()).data;
    console.info("Query", [QueryKeyReportBranchDaily]);
    return response["data"] as GetBranchDailyModel;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKeyReportBranchDaily, workDate, branchId],
    queryFn: () => apiFn(workDate, branchId),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
    staleTime: appConfig.staleTime,
  });
}


export function useQyGetReportBranch(
  onSuccess?:
    | ((data: GetBranchDailyModelV2) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: DashboardPayload) => {
    await checkSession();

    showProgress();

    const operation = await getBranchDailyV2(
      payload.startDate,
      payload.endDate,
      payload.branchId ?? "",
      authHeaders(),
    );
    const response = (await operation()).data;
    console.info("Query", [QueryKeyReportBranchDaily]);
    return response["data"] as GetBranchDailyModelV2;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyReportBranchDaily);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetReportPerformance(
  onSuccess?:
    | ((data: GetDailyPerformanceModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (payload: DashboardPayload) => {
    await checkSession();

    showProgress();
    const operation = await getDailyPerformance(
      payload.startDate,
      payload.endDate,
      payload.branchId,
      authHeaders(),
    );
    const response = (await operation()).data;
    console.info("Query", [QueryKeyReportDailyPerformance]);
    return response["data"] as GetDailyPerformanceModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyReportDailyPerformance);
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetReportBranchDailyV2(
  startDate: string,
  endDate: string,
  selectedBranch?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetBranchDailyModelV2) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const _branchId = ALL ? undefined : selectedBranch;
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    startDate: string,
    endDate: string,
    branchId?: string,
  ) => {
    await checkSession();

    showProgress();
    console.info("Branch ID in Query", { _branchId, branchId });
    debugger;
    const operation = await getBranchDailyV2(
      startDate,
      endDate,
      branchId ?? "",
      authHeaders(),
    );
    const response = (await operation()).data;
    console.info("Query", [QueryKeyReportBranchDaily]);
    return response["data"] as GetBranchDailyModelV2;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKeyReportBranchDaily, startDate, endDate, _branchId],
    queryFn: () => apiFn(startDate, endDate, _branchId),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
    staleTime: appConfig.staleTime,
  });
}

export function useQyGetReportDailyPerformance(
  startDate: string,
  endDate: string,
  branchId: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetDailyPerformanceModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    startDate: string,
    endDate: string,
    branchId: string,
  ) => {
    await checkSession();

    showProgress();
    const operation = await getDailyPerformance(
      startDate,
      endDate,
      branchId,
      authHeaders(),
    );
    const response = (await operation()).data;
    console.info("Query", [QueryKeyReportDailyPerformance]);
    return response["data"] as GetDailyPerformanceModel;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKeyReportDailyPerformance, startDate, endDate, branchId],
    queryFn: () => apiFn(startDate, endDate, branchId),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
    staleTime: appConfig.staleTime,
  });
}

export function useQyGetPerformanceRanking(
  startDate: string,
  endDate: string,
  branchId?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetPerformanceRankingResponseModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const _branchId = ALL ? undefined : branchId;
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    startDate: string,
    endDate: string,
    branchId?: string,
  ) => {
    await checkSession();

    showProgress();
    const operation = await getPerformanceRanking(
      startDate,
      endDate,
      branchId,
      undefined,
      undefined,
      99,
      0,
      authHeaders(),
    );
    const response = (await operation()).data;
    console.info("Query", [QueryKeyReportPerformanceRanking]);
    return response["data"] as GetPerformanceRankingResponseModel;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKeyReportPerformanceRanking, startDate, endDate, _branchId],
    queryFn: () => apiFn(startDate, endDate, _branchId),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
    staleTime: appConfig.staleTime,
  });
}

export function useQyGetLateRanking(
  startDate: string,
  endDate: string,
  branchId?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetLateRankingResponse) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    startDate: string,
    endDate: string,
    branchId?: string,
  ) => {
    await checkSession();

    showProgress();
    const operation = await getLateRanking(
      startDate,
      endDate,
      branchId,
      undefined,
      undefined,
      99,
      0,
      authHeaders(),
    );
    const response = (await operation()).data;
    console.info("Query", [QueryKeyReportGetLateRanking]);
    return response["data"] as GetLateRankingResponse;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKeyReportGetLateRanking, startDate, endDate, branchId],
    queryFn: () => apiFn(startDate, endDate, branchId),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
    staleTime: appConfig.staleTime,
  });
}

export function useQyGetOvertimeRanking(
  startDate: string,
  endDate: string,
  branchId?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetLateRankingResponse) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    startDate: string,
    endDate: string,
    branchId?: string,
  ) => {
    await checkSession();

    showProgress();
    const operation = await getOvertimeRanking(
      startDate,
      endDate,
      branchId,
      undefined,
      undefined,
      99,
      0,
      authHeaders(),
    );
    const response = (await operation()).data;
    console.info("Query", [QueryKeyReportGetOvertimeRanking]);
    return response["data"] as GetLateRankingResponse;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKeyReportGetOvertimeRanking, startDate, endDate, branchId],
    queryFn: () => apiFn(startDate, endDate, branchId),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
    staleTime: appConfig.staleTime,
  });
}

export function useQyGetAbsentRanking(
  startDate: string,
  endDate: string,
  branchId?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetLateRankingResponse) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    startDate: string,
    endDate: string,
    branchId?: string,
  ) => {
    await checkSession();

    showProgress();
    const operation = await getAbsentRanking(
      startDate,
      endDate,
      branchId,
      undefined,
      undefined,
      99,
      0,
      authHeaders(),
    );
    const response = (await operation()).data;
    console.info("Query", [QueryKeyReportGetAbsentRanking]);
    return response["data"] as GetLateRankingResponse;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKeyReportGetAbsentRanking, startDate, endDate, branchId],
    queryFn: () => apiFn(startDate, endDate, branchId),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
    staleTime: appConfig.staleTime,
  });
}

export function useQyGetAmendedRanking(
  startDate: string,
  endDate: string,
  branchId?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetLateRankingResponse) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    startDate: string,
    endDate: string,
    branchId?: string,
  ) => {
    await checkSession();

    showProgress();
    const operation = await getAmendedRanking(
      startDate,
      endDate,
      branchId,
      undefined,
      undefined,
      99,
      0,
      authHeaders(),
    );
    const response = (await operation()).data;
    console.info("Query", [QueryKeyReportGetAmendRanking]);
    return response["data"] as GetLateRankingResponse;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKeyReportGetAmendRanking, startDate, endDate, branchId],
    queryFn: () => apiFn(startDate, endDate, branchId),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
    staleTime: appConfig.staleTime,
  });
}

export function useQyGetOnLeaveRanking(
  startDate: string,
  endDate: string,
  branchId?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetLateRankingResponse) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    startDate: string,
    endDate: string,
    branchId?: string,
  ) => {
    await checkSession();

    showProgress();
    const operation = await getLeaveRanking(
      startDate,
      endDate,
      branchId,
      undefined,
      undefined,
      99,
      0,
      authHeaders(),
    );
    const response = (await operation()).data;
    console.info("Query", [QueryKeyReportGetLeaveRanking]);
    return response["data"] as GetLateRankingResponse;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKeyReportGetLeaveRanking, startDate, endDate, branchId],
    queryFn: () => apiFn(startDate, endDate, branchId),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
    staleTime: appConfig.staleTime,
  });
}

export function useQyGetUndertimeRanking(
  startDate: string,
  endDate: string,
  branchId?: string,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetLateRankingResponse) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    startDate: string,
    endDate: string,
    branchId?: string,
  ) => {
    await checkSession();

    showProgress();
    const operation = await getUndertimeRanking(
      startDate,
      endDate,
      branchId,
      undefined,
      undefined,
      99,
      0,
      authHeaders(),
    );
    const response = (await operation()).data;
    console.info("Query", [QueryKeyReportGetUndertimeRanking]);
    return response["data"] as GetLateRankingResponse;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKeyReportGetUndertimeRanking, startDate, endDate, branchId],
    queryFn: () => apiFn(startDate, endDate, branchId),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
    staleTime: appConfig.staleTime,
  });
}
